import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { GridCompatibilityProcessStatus } from 'src/app/+grid-compatibility/entities/enums/gridCompatibilityProcessStatus';
import { GridCompatibilityService } from 'src/app/+grid-compatibility/services/grid-compatibility.service';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { ErrorService } from 'src/app/services/error/error.service';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';

@Component({
  selector: 'app-regep-jurisdiction',
  templateUrl: './regep-jurisdiction.component.html',
  styleUrls: ['./regep-jurisdiction.component.scss'],
})
export class RegepJurisdictionComponent extends DestroyableComponent implements OnInit {
  regepJurisdictionForm: FormGroup;

  @Input() isTotalFeedInPowerAbove: boolean;
  @Input() gridCompOrderNumber: string;
  @Output() regEpJurisdictionDataChange = new EventEmitter<{
    externalId?: string | null;
    totalFeedInPowerIsAboveCentralGridThreshold: boolean;
  }>();

  constructor(
    private gridCompApplicationService: GridCompatibilityService,
    private errorService: ErrorService,
    private netzTranslateService: NetzTranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.regepJurisdictionForm = new FormGroup({
      totalFeedInPowerIsAboveCentralGridThreshold: new FormControl(false),
    });

    this.preventLeak(this.regepJurisdictionForm.controls.totalFeedInPowerIsAboveCentralGridThreshold.valueChanges)
      .pipe(
        distinctUntilChanged(),
        tap(totalFeedInPowerIsAboveCentralGridThreshold =>
          this.regEpJurisdictionDataChange.emit({
            totalFeedInPowerIsAboveCentralGridThreshold,
            externalId: totalFeedInPowerIsAboveCentralGridThreshold ? this.gridCompOrderNumber : null,
          })
        )
      )
      .subscribe(totalFeedInPowerIsAboveCentralGridThreshold =>
        this.toggleGridCompAssessmentOrderNumberControl(totalFeedInPowerIsAboveCentralGridThreshold)
      );

    if (this.isTotalFeedInPowerAbove) {
      this.regepJurisdictionForm.setValue({ totalFeedInPowerIsAboveCentralGridThreshold: this.isTotalFeedInPowerAbove });
      this.regepJurisdictionForm.updateValueAndValidity();
    }
  }

  private toggleGridCompAssessmentOrderNumberControl(totalFeedInPowerIsAboveCentralGridThreshold: boolean) {
    if (totalFeedInPowerIsAboveCentralGridThreshold) {
      this.regepJurisdictionForm.addControl(
        'gridCompAssessmentOrderNumber',
        new FormControl(this.gridCompOrderNumber || '', [Validators.required], [this.validateExternalId()])
      );
      this.regepJurisdictionForm.updateValueAndValidity();

      this.preventLeak(this.regepJurisdictionForm.controls.gridCompAssessmentOrderNumber.valueChanges)
        .pipe(debounceTime(500))
        .subscribe(value => {
          const extId = this.regepJurisdictionForm.controls.gridCompAssessmentOrderNumber.errors?.invalidExternalId ? null : value;

          if (extId == null) {
            this.errorService.throwErrorIfFailure(
              {
                result: 'error',
                message: this.netzTranslateService.translateString('regep.invalid-external-id'),
              },
              true
            );
          }
          this.regEpJurisdictionDataChange.emit({
            externalId: extId,
            totalFeedInPowerIsAboveCentralGridThreshold: this.regepJurisdictionForm.value.totalFeedInPowerIsAboveCentralGridThreshold,
          });
        });
    } else {
      this.regepJurisdictionForm.removeControl('gridCompAssessmentOrderNumber');
      this.regepJurisdictionForm.updateValueAndValidity();
    }
  }

  private validateExternalId(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) return null;

      return this.preventLeak(
        this.gridCompApplicationService.getByExternalIdAndStatus(control.value, GridCompatibilityProcessStatus.FeedInPermissionIssued)
      ).pipe(
        map(app => (app == null ? { invalidExternalId: true } : null)),
        catchError(() => of(null))
      );
    };
  }
}
